import React from "react";
import Reveal from "./Reveal";
import { useT } from "@/hooks/useT";
import {
  AlertTriangle,
  Calendar,
  Receipt,
  Globe,
  FileSpreadsheet,
  MessageCircle,
  Sparkles,
  CheckCircle2,
  BedDouble,
  ShoppingBag,
  Repeat2,
  FileText,
  BarChart3,
  Smartphone,
  PhoneCall,
  KeyRound,
  Building2,
  Wallet,
  DoorClosed,
  CreditCard,
} from "lucide-react";

/**
 * Two stacked sections:
 *  1) Feature ecosystem grid — "Një platformë. Të gjitha funksionet." (light)
 *  2) Cinematic chaos vs. control comparison (dark navy hero-style background)
 */
export default function ChaosVsControl() {
  return (
    <>
      <FeatureGridSection />
      <ChaosComparisonSection />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   1) FEATURE ECOSYSTEM GRID — bento layout with visual hero cards
   ───────────────────────────────────────────────────────────────────── */
function FeatureGridSection() {
  const t = useT();
  return (
    <section
      className="relative bg-[#F8FAFC] py-20 md:py-28"
      data-testid="feature-grid-section"
    >
      <div className="container-akm">
        <Reveal variant="up" className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#EA580C]">
            <Sparkles size={12} className="text-[#FF7A00]" />
            {t("Ekosistemi", "Ecosystem")}
          </span>
          <h3
            className="mt-3 font-display font-extrabold tracking-[-0.035em] text-[#0F172A]"
            style={{
              fontSize: "clamp(1.75rem, 1rem + 1.6vw, 2.75rem)",
              lineHeight: 1.1,
            }}
            data-testid="feature-grid-heading"
          >
            {t("Një platformë.", "One platform.")}{" "}
            <span className="text-[#F26322]">{t("Të gjitha funksionet.", "Every feature.")}</span>
          </h3>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-[1.7] text-[#64748B]">
            {t(
              "Çdo modul i ndërtuar për hotelin tuaj — i lidhur me të tjerët, pa siklet integrimi.",
              "Every module built for your hotel — natively connected, with zero integration hassle."
            )}
          </p>
        </Reveal>

        {/* ─── Uniform bento grid — equal-height rows ─── */}
        <div className="mt-12 grid auto-rows-fr grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:gap-4">
          {/* PMS & Rezervime */}
          <FeatureCard
            icon={BedDouble}
            title={t("PMS & Rezervime", "PMS & Reservations")}
            desc={t("Recepsioni dhe rezervimet në një sistem qendror.", "Front desk and reservations in one central system.")}
            accent="orange"
            testId="feature-card-pms"
            delay={0}
          >
            <MiniCalendar />
          </FeatureCard>

          {/* Booking Engine */}
          <FeatureCard
            icon={ShoppingBag}
            title="Booking Engine"
            desc={t("Rezervime direkte në website-in tuaj — pa komisione.", "Direct bookings from your website — zero commissions.")}
            accent="sky"
            testId="feature-card-booking-engine"
            delay={60}
          >
            <BadgeStrip items={[t("Pa komision", "No commission"), "24/7", "Mobile-first"]} accent="sky" />
          </FeatureCard>

          {/* Channel Manager */}
          <FeatureCard
            icon={Repeat2}
            title="Channel Manager"
            desc={t("Booking.com, Airbnb, Expedia në një vend.", "Booking.com, Airbnb, Expedia in one place.")}
            accent="orange"
            testId="feature-card-channel-manager"
            delay={120}
          >
            <OtaDots />
          </FeatureCard>

          {/* OTA Sync */}
          <FeatureCard
            icon={Globe}
            title="OTA Sync"
            desc={t("Sinkronizim automatik me të gjithë kanalet.", "Automatic sync across every channel.")}
            accent="emerald"
            testId="feature-card-ota-sync"
            delay={180}
          >
            <SyncBadge />
          </FeatureCard>

          {/* Faturim */}
          <FeatureCard
            icon={FileText}
            title={t("Faturim & Fiskalizim", "Billing & Fiscalization")}
            desc={t("Fatura të menjëhershme dhe gjithmonë në rregull.", "Instant invoices, always compliant.")}
            accent="navy"
            testId="feature-card-faturim-fiskalizim"
            delay={240}
          >
            <InvoiceBadge />
          </FeatureCard>

          {/* Menaxhim Dhomash */}
          <FeatureCard
            icon={KeyRound}
            title={t("Menaxhim i Dhomave", "Room Management")}
            desc={t("Statuset dhe pastrimi në kohë reale.", "Real-time room status and housekeeping.")}
            accent="amber"
            testId="feature-card-menaxhim-i-dhomave"
            delay={300}
          >
            <RoomStatusPreview />
          </FeatureCard>

          {/* Raporte */}
          <FeatureCard
            icon={BarChart3}
            title={t("Raporte & Analiza", "Reports & Analytics")}
            desc={t("Të ardhura, ADR, RevPAR dhe trende.", "Revenue, ADR, RevPAR and trends.")}
            accent="orange"
            testId="feature-card-raporte-analiza"
            delay={360}
          >
            <MiniChart />
          </FeatureCard>

          {/* Smart Hotel */}
          <FeatureCard
            icon={Building2}
            title="Smart Hotel"
            desc={t("Pajisje IoT dhe çelësa elektronikë.", "IoT devices and keyless entry.")}
            accent="violet"
            testId="feature-card-smart-hotel"
            delay={420}
          >
            <BadgeStrip items={["IoT", "Smart Key", "IPTV"]} accent="violet" />
          </FeatureCard>

          {/* Komunikim */}
          <FeatureCard
            icon={PhoneCall}
            title={t("Komunikim me Klientin", "Guest Communication")}
            desc={t("WhatsApp, email dhe SMS të integruara.", "WhatsApp, email and SMS integrated.")}
            accent="emerald"
            testId="feature-card-komunikim-me-klientin"
            delay={480}
          >
            <ChatPreview />
          </FeatureCard>

          {/* Online Check-In */}
          <FeatureCard
            icon={Smartphone}
            title="Online Check-In"
            desc={t("Klientët bëjnë check-in direkt nga celulari.", "Guests check in straight from their phone.")}
            accent="sky"
            testId="feature-card-online-check-in"
            delay={540}
          >
            <BadgeStrip items={["QR", "ID Scan", "e-Sign"]} accent="sky" />
          </FeatureCard>
        </div>
      </div>
    </section>
  );
}

/* ─── Color tokens per accent ─── */
const ACCENTS = {
  orange:  { iconBg: "from-[#FFF1E5] to-[#FFE0CC]", iconRing: "ring-[#FFD9B5]/60", iconColor: "text-[#EA580C]", topGlow: "via-[#FF9A3D]" },
  sky:     { iconBg: "from-[#E0F2FE] to-[#BAE6FD]", iconRing: "ring-[#7DD3FC]/50",  iconColor: "text-[#0284C7]", topGlow: "via-[#38BDF8]" },
  emerald: { iconBg: "from-[#D1FAE5] to-[#A7F3D0]", iconRing: "ring-[#6EE7B7]/50",  iconColor: "text-[#059669]", topGlow: "via-[#34D399]" },
  amber:   { iconBg: "from-[#FEF3C7] to-[#FDE68A]", iconRing: "ring-[#FCD34D]/50",  iconColor: "text-[#D97706]", topGlow: "via-[#FBBF24]" },
  violet:  { iconBg: "from-[#EDE9FE] to-[#DDD6FE]", iconRing: "ring-[#C4B5FD]/50",  iconColor: "text-[#7C3AED]", topGlow: "via-[#A78BFA]" },
  navy:    { iconBg: "from-[#E2E8F0] to-[#CBD5E1]", iconRing: "ring-[#94A3B8]/40",  iconColor: "text-[#0F172A]", topGlow: "via-[#64748B]" },
};

/* ─── Unified card ─── */
function FeatureCard({
  className = "",
  icon: Icon,
  title,
  desc,
  accent = "orange",
  testId,
  delay = 0,
  children,
}) {
  const a = ACCENTS[accent];
  return (
    <Reveal variant="up" delay={delay} className="h-full">
    <div
      data-testid={testId}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-[#FFD9B5] hover:shadow-[0_18px_40px_-18px_rgba(242,99,34,0.28)] ${className}`}
    >
      <span
        className={`pointer-events-none absolute inset-x-0 -top-px h-[2px] bg-gradient-to-r from-transparent ${a.topGlow} to-transparent opacity-0 transition-opacity group-hover:opacity-100`}
        aria-hidden
      />

      <div className="flex items-start justify-between">
        <span
          className={`inline-grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br ${a.iconBg} ring-1 ${a.iconRing} ${a.iconColor} transition-transform group-hover:scale-105`}
        >
          <Icon size={18} strokeWidth={2.1} />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#94A3B8] opacity-0 transition-opacity group-hover:opacity-100">
          {"Module"}
        </span>
      </div>

      <h4 className="mt-4 font-display text-[15px] font-bold leading-tight tracking-tight text-[#0F172A]">
        {title}
      </h4>
      <p className="mt-1.5 text-[12.5px] leading-[1.55] text-[#64748B]">
        {desc}
      </p>

      {children && (
        <div className="mt-auto pt-3">
          {children}
        </div>
      )}
    </div>
    </Reveal>
  );
}

/* ─── Inline visuals — compact ─── */
function MiniCalendar() {
  const t = useT();
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-[#FAFBFC] p-2">
      <div className="flex items-center justify-between">
        <span className="text-[9.5px] font-semibold text-[#0F172A]">Qershor</span>
        <span className="text-[9px] font-semibold text-emerald-600">● 84%</span>
      </div>
      <div className="mt-1.5 grid grid-cols-7 gap-0.5">
        {Array.from({ length: 14 }).map((_, i) => {
          const booked = [1, 2, 5, 6, 7, 10, 11, 12].includes(i);
          const today = i === 6;
          return (
            <span
              key={i}
              className={`h-3.5 rounded-sm ${
                today
                  ? "bg-[#0F172A]"
                  : booked
                  ? "bg-[#FFE0CC]"
                  : "bg-white ring-1 ring-[#F1F5F9]"
              }`}
            />
          );
        })}
      </div>
    </div>
  );
}

function MiniChart() {
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-[#FAFBFC] p-2.5">
      <div className="flex items-center justify-between">
        <span className="font-display text-[14px] font-extrabold text-[#0F172A]">€82.4k</span>
        <span className="text-[9.5px] font-semibold text-emerald-600">▲ +18%</span>
      </div>
      <svg viewBox="0 0 160 32" className="mt-1 h-8 w-full">
        <defs>
          <linearGradient id="mini-rev" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#F26322" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#F26322" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,24 C20,18 35,22 55,12 C75,4 95,18 120,8 L160,2 L160,32 L0,32 Z"
          fill="url(#mini-rev)"
        />
        <path
          d="M0,24 C20,18 35,22 55,12 C75,4 95,18 120,8 L160,2"
          fill="none"
          stroke="#F26322"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function BadgeStrip({ items, accent = "orange" }) {
  const colorMap = {
    orange:  "bg-[#FFF1E5] text-[#EA580C] ring-[#FFD9B5]",
    sky:     "bg-[#E0F2FE] text-[#0284C7] ring-[#7DD3FC]/60",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    violet:  "bg-violet-50 text-violet-700 ring-violet-200",
    amber:   "bg-amber-50 text-amber-700 ring-amber-200",
    navy:    "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((label) => (
        <span
          key={label}
          className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[9.5px] font-semibold ring-1 ${colorMap[accent]}`}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function SyncBadge() {
  const t = useT();
  return (
    <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-2 py-1.5 ring-1 ring-emerald-200">
      <span className="relative grid h-5 w-5 place-items-center rounded-full bg-emerald-500/15 text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(34,197,94,0.7)]" />
      </span>
      <div className="leading-tight">
        <div className="text-[10px] font-semibold text-emerald-800">Live</div>
        <div className="text-[8.5px] text-emerald-700/70">{t("Sinkron çdo 30s","Sync every 30s")}</div>
      </div>
    </div>
  );
}

function InvoiceBadge() {
  const t = useT();
  return (
    <div className="rounded-lg bg-slate-50 p-2 ring-1 ring-slate-200">
      <div className="flex items-center justify-between text-[9.5px]">
        <span className="text-[#64748B]">{t("Fatura","Invoice")} #2026-0142</span>
        <span className="font-semibold text-emerald-600">● {t("Fiskalizuar","Fiscalized")}</span>
      </div>
      <div className="mt-0.5 font-display text-[13px] font-extrabold text-[#0F172A]">
        €642.00
      </div>
    </div>
  );
}

function OtaDots() {
  const t = useT();
  const OTAS = [
    { bg: "#003580", mark: "B." },
    { bg: "#FF5A5F", mark: "A" },
    { bg: "#FFC72C", mark: "EX", color: "#191E3B" },
    { bg: "#5392F9", mark: "Ag" },
  ];
  return (
    <div className="flex items-center gap-1.5">
      {OTAS.map((o, i) => (
        <span
          key={i}
          className="grid h-6 w-6 place-items-center rounded-md text-[9px] font-extrabold text-white ring-1 ring-black/5"
          style={{ background: o.bg, color: o.color || "#fff" }}
        >
          {o.mark}
        </span>
      ))}
      <span className="ml-1 text-[9.5px] font-semibold text-emerald-600">
        ● {t("Sinkron","Synced")}
      </span>
    </div>
  );
}

function RoomStatusPreview() {
  const t = useT();
  const STATUS = ["occ","occ","clean","clean","occ","free","occ","clean","occ","occ","free","clean"];
  const colors = {
    occ: "bg-[#FFE0CC]",
    clean: "bg-emerald-200",
    free: "bg-[#E2E8F0]",
  };
  return (
    <div>
      <div className="grid grid-cols-6 gap-0.5">
        {STATUS.map((s, i) => (
          <span key={i} className={`h-3.5 rounded-sm ${colors[s]}`} />
        ))}
      </div>
      <div className="mt-1.5 flex items-center gap-2 text-[9px] text-[#64748B]">
        <span className="inline-flex items-center gap-0.5"><span className="h-1 w-1 rounded-full bg-[#EA580C]" /> {t("Zëna","Occ.")}</span>
        <span className="inline-flex items-center gap-0.5"><span className="h-1 w-1 rounded-full bg-emerald-500" /> {t("Pastruar","Cleaned")}</span>
        <span className="inline-flex items-center gap-0.5"><span className="h-1 w-1 rounded-full bg-[#94A3B8]" /> {t("Lirë","Free")}</span>
      </div>
    </div>
  );
}

function ChatPreview() {
  const t = useT();
  return (
    <div className="space-y-1">
      <div className="flex items-start gap-1.5">
        <span className="grid h-4 w-4 place-items-center rounded-full bg-emerald-100 text-[8px] font-bold text-emerald-700">A</span>
        <span className="rounded-md rounded-tl-sm bg-emerald-50 px-1.5 py-0.5 text-[9.5px] text-[#0F172A]">
          {t("Check-in është gati 🌿","Check-in is ready 🌿")}
        </span>
      </div>
      <div className="flex items-start justify-end">
        <span className="rounded-md rounded-tr-sm bg-[#0F172A] px-1.5 py-0.5 text-[9.5px] text-white">
          {t("Po vij!","On my way!")}
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   2) CHAOS vs CONTROL — cinematic dark navy
   ───────────────────────────────────────────────────────────────────── */
function ChaosComparisonSection() {
  const t = useT();
  return (
    <section
      className="relative isolate overflow-hidden bg-[#071326] py-20 md:py-28"
      data-testid="chaos-vs-control"
    >
      {/* Cinematic background (matches HeroImmersive) */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#0F2647_0%,#071326_55%,#040A18_100%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -top-40 right-[-12%] h-[820px] w-[820px] rounded-full bg-[radial-gradient(closest-side,rgba(255,122,0,0.28),transparent_70%)] blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-[-25%] left-[-12%] h-[640px] w-[640px] rounded-full bg-[radial-gradient(closest-side,rgba(56,109,212,0.22),transparent_70%)] blur-3xl"
        aria-hidden
      />
      <DarkParticles />
      {/* Bottom fade transition */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[#040A18]"
        aria-hidden
      />

      <div className="container-akm relative">
        <Reveal variant="up" className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-300/90 backdrop-blur-sm">
            <Sparkles size={12} className="text-[#FF9A3D]" />
            {t("Pa Akomoda · Me Akomoda", "Without Akomoda · With Akomoda")}
          </span>
          <h2
            className="mt-4 font-display font-extrabold tracking-[-0.035em] text-white"
            style={{
              fontSize: "clamp(1.85rem, 1.1rem + 1.6vw, 2.75rem)",
              lineHeight: 1.1,
            }}
            data-testid="chaos-heading"
          >
            {t("Nga kaosi në", "From chaos to")}{" "}
            <span className="bg-gradient-to-r from-[#FFB875] via-[#FF8A3D] to-[#FF7A00] bg-clip-text text-transparent">
              {t("kontroll të plotë", "complete control")}
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-[1.7] text-white/60">
            {t(
              "Shihni vizualisht si hotelet kalojnë nga sisteme të shkëputura dhe punë manuale në një platformë të vetme dhe të organizuar me Akomoda.",
              "See how hotels move from disconnected systems and manual work to one unified, organised platform with Akomoda."
            )}
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-2 lg:gap-8">
          <Reveal variant="left" delay={120}><BeforePanel /></Reveal>
          <Reveal variant="right" delay={240}><AfterPanel /></Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── Subtle dark particles ─── */
function DarkParticles() {
  const dots = [
    { l: "10%", t: "18%", s: 2, o: 0.3 },
    { l: "22%", t: "70%", s: 1.5, o: 0.22 },
    { l: "38%", t: "12%", s: 1.5, o: 0.28 },
    { l: "58%", t: "82%", s: 2, o: 0.32 },
    { l: "72%", t: "20%", s: 1.5, o: 0.26 },
    { l: "86%", t: "62%", s: 2, o: 0.3 },
    { l: "92%", t: "30%", s: 1.5, o: 0.28 },
  ];
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: d.l,
            top: d.t,
            width: `${d.s}px`,
            height: `${d.s}px`,
            opacity: d.o,
            boxShadow: `0 0 ${d.s * 4}px rgba(255,255,255,0.4)`,
          }}
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   LEFT — "Para Akomoda" (chaos) — dark glass card
   ───────────────────────────────────────────────────────────────────── */
function BeforePanel() {
  const t = useT();
  const NODES = [
    { icon: Calendar,         label: t("Rezervime","Bookings"),           pos: "top-[6%] left-[6%]"     },
    { icon: Receipt,          label: t("Fatura","Invoices"),              pos: "top-[6%] right-[6%]"    },
    { icon: Globe,            label: t("Booking Extranet","Booking Extranet"), pos: "top-[30%] left-[-2%]" },
    { icon: Globe,            label: t("Airbnb Extranet","Airbnb Extranet"),   pos: "top-[30%] right-[-2%]" },
    { icon: Wallet,           label: t("Financa","Finance"),              pos: "top-[58%] left-[-2%]"   },
    { icon: DoorClosed,       label: t("Kontrolli Dhomave","Room Controls"), pos: "top-[58%] right-[-2%]" },
    { icon: FileSpreadsheet,  label: t("Excel / Sheets","Excel / Sheets"),pos: "bottom-[6%] left-[8%]"  },
    { icon: Building2,        label: t("Smart Hotel","Smart Hotel"),      pos: "bottom-[6%] left-1/2 -translate-x-1/2" },
    { icon: MessageCircle,    label: "WhatsApp",                          pos: "bottom-[6%] right-[8%]" },
  ];

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[rgba(13,28,52,0.55)] p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl"
      data-testid="chaos-before-panel"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_50%,rgba(244,63,94,0.10),transparent_70%)]"
        aria-hidden
      />

      <div className="relative flex items-center justify-between">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-rose-300 ring-1 ring-rose-400/25">
            <AlertTriangle size={11} /> {t("Para Akomoda", "Before Akomoda")}
          </span>
          <h3 className="mt-3 font-display text-[22px] font-extrabold tracking-tight text-white">
            {t("Sisteme të shkëputura", "Disconnected systems")}
          </h3>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-rose-300/70">
          {t("Kaos operativ", "Operational chaos")}
        </span>
      </div>

      <div className="relative mt-6 aspect-[5/4] w-full">
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          viewBox="0 0 100 75"
          preserveAspectRatio="none"
          aria-hidden
        >
          {/* All paths converge at the warning icon center (50, 37.5).
              We stop a bit short of center so lines visually meet the icon edge. */}
          {[
            "M 14 12 Q 32 24 50 37",   // Rezervime
            "M 86 12 Q 68 24 50 37",   // Fatura
            "M 4 28 Q 25 32 48 36",    // Booking Extranet
            "M 96 28 Q 75 32 52 36",   // Airbnb Extranet
            "M 4 50 Q 25 42 48 39",    // Financa
            "M 96 50 Q 75 42 52 39",   // Kontrolli Dhomave
            "M 14 70 Q 32 54 49 41",   // Excel
            "M 50 70 Q 50 54 50 42",   // Smart Hotel
            "M 86 70 Q 68 54 51 41",   // WhatsApp
          ].map((d, i) => (
            <path
              key={i}
              d={d}
              fill="none"
              stroke="#FB7185"
              strokeOpacity="0.55"
              strokeWidth="0.45"
              strokeDasharray="0.9 1.4"
              strokeLinecap="round"
            />
          ))}
        </svg>

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="relative">
            <div className="absolute inset-0 -m-2 rounded-full bg-rose-400/35 blur-xl" aria-hidden />
            <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-rose-400/30 bg-[rgba(13,28,52,0.85)] shadow-[0_12px_30px_-8px_rgba(244,63,94,0.5)]">
              <AlertTriangle size={26} className="text-rose-400" />
            </div>
          </div>
        </div>

        {NODES.map(({ icon: Icon, label, pos }) => (
          <div
            key={label}
            className={`absolute ${pos} flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.05] px-2.5 py-1.5 backdrop-blur-md`}
          >
            <span className="grid h-6 w-6 place-items-center rounded-md bg-white/[0.08] text-white/60">
              <Icon size={12} />
            </span>
            <span className="whitespace-nowrap text-[11px] font-semibold text-white/80">
              {label}
            </span>
          </div>
        ))}
      </div>

      <p className="relative mt-6 text-[13.5px] leading-[1.6] text-white/65">
        <span className="font-semibold text-white">{t("Shumë sisteme.", "Too many systems.")}</span>{" "}
        {t("Shumë punë manuale.", "Too much manual work.")}{" "}
        <span className="font-semibold text-rose-300">{t("Shumë gabime.", "Too many mistakes.")}</span>
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   RIGHT — "Me Akomoda" (centralized ecosystem) — dark glass
   ───────────────────────────────────────────────────────────────────── */
function AfterPanel() {
  const t = useT();
  /* 8 nodes positioned symmetrically (4 left, 4 right) around the central hub.
     Each path goes from the node toward the hub at (50, 37.5). */
  const NODES = [
    { icon: BedDouble,    label: t("PMS & Rezervime", "PMS & Bookings"), pos: "top-[4%] left-[-2%]",   path: "M 8 10 L 49 36" },
    { icon: Repeat2,      label: "Channel Manager",                       pos: "top-[4%] right-[-2%]",  path: "M 92 10 L 51 36" },
    { icon: FileText,     label: t("Faturim", "Billing"),                 pos: "top-[28%] left-[-2%]",  path: "M 4 27 L 48 36" },
    { icon: BarChart3,    label: t("Raporte", "Reports"),                 pos: "top-[28%] right-[-2%]", path: "M 96 27 L 52 36" },
    { icon: DoorClosed,   label: t("Kontrolli Dhomave", "Room Controls"), pos: "top-[52%] left-[-2%]",  path: "M 4 48 L 48 39" },
    { icon: CreditCard,   label: t("Kartat", "Cards"),                    pos: "top-[52%] right-[-2%]", path: "M 96 48 L 52 39" },
    { icon: Building2,    label: "Smart Hotel",                           pos: "bottom-[4%] left-[-2%]",path: "M 8 66 L 49 41" },
    { icon: PhoneCall,    label: t("Komunikim", "Comms"),                 pos: "bottom-[4%] right-[-2%]",path:"M 92 66 L 51 41" },
  ];

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/[0.10] bg-[rgba(13,28,52,0.55)] p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl"
      data-testid="chaos-after-panel"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_55%_at_50%_50%,rgba(255,138,61,0.16),transparent_70%)]"
        aria-hidden
      />
      <span
        className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-b from-[#FF9A3D]/25 via-transparent to-transparent opacity-50 [mask:linear-gradient(black,transparent_40%)]"
        aria-hidden
      />

      <div className="relative flex items-center justify-between">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-emerald-300 ring-1 ring-emerald-400/25">
            <CheckCircle2 size={11} /> {t("Me Akomoda", "With Akomoda")}
          </span>
          <h3 className="mt-3 font-display text-[22px] font-extrabold tracking-tight text-white">
            {t("Një platformë e vetme", "One single platform")}
          </h3>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300/80">
          {t("Kontroll i plotë", "Full control")}
        </span>
      </div>

      <div className="relative mt-6 aspect-[5/4] w-full">
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          viewBox="0 0 100 75"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="after-line-dark" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#FFB875" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#FF7A00" stopOpacity="0.25" />
            </linearGradient>
            <radialGradient id="particle-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FFD9B5" stopOpacity="1" />
              <stop offset="40%" stopColor="#FF9A3D" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#FF7A00" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Static base lines + animated particles per node path */}
          {NODES.map(({ path }, i) => (
            <g key={i}>
              <path
                id={`after-path-${i}`}
                d={path}
                fill="none"
                stroke="url(#after-line-dark)"
                strokeWidth="0.5"
                strokeLinecap="round"
              />
              {/* Glow particle traveling toward the hub */}
              <circle r="1.4" fill="url(#particle-glow)">
                <animateMotion
                  dur={`${2.4 + (i % 4) * 0.4}s`}
                  begin={`${(i * 0.25).toFixed(2)}s`}
                  repeatCount="indefinite"
                  rotate="auto"
                >
                  <mpath href={`#after-path-${i}`} />
                </animateMotion>
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.15;0.85;1"
                  dur={`${2.4 + (i % 4) * 0.4}s`}
                  begin={`${(i * 0.25).toFixed(2)}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          ))}
        </svg>

        {/* Central glowing Akomoda hub with pulsing rings */}
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
          <div className="relative">
            {/* Pulsing rings */}
            <span
              className="pointer-events-none absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#FF9A3D]/40"
              style={{ animation: "akomoda-ping 2.4s ease-out infinite" }}
              aria-hidden
            />
            <span
              className="pointer-events-none absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#FF9A3D]/30"
              style={{ animation: "akomoda-ping 2.4s ease-out infinite", animationDelay: "0.8s" }}
              aria-hidden
            />

            <div className="absolute inset-0 -m-5 rounded-full bg-[#FF7A00]/45 blur-2xl" aria-hidden />
            <div className="relative grid h-20 w-20 place-items-center rounded-3xl bg-white shadow-[0_22px_44px_-10px_rgba(234,88,12,0.55),inset_0_1px_0_rgba(255,255,255,0.4)] ring-1 ring-white/20">
              <img
                src="/akomoda-icon-color.png"
                alt="Akomoda"
                className="h-12 w-12 object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <span
              className="pointer-events-none absolute -inset-2 rounded-full border border-[#FF9A3D]/35"
              aria-hidden
            />
            <span
              className="pointer-events-none absolute -inset-4 rounded-full border border-[#FF9A3D]/15"
              aria-hidden
            />
          </div>
        </div>

        {NODES.map(({ icon: Icon, label, pos }) => (
          <div
            key={label}
            className={`absolute ${pos} flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.06] px-2.5 py-1.5 backdrop-blur-md`}
          >
            <span className="grid h-6 w-6 place-items-center rounded-md bg-[#FF9A3D]/15 text-[#FFB875]">
              <Icon size={12} />
            </span>
            <span className="whitespace-nowrap text-[11px] font-semibold text-white/90">
              {label}
            </span>
            <span className="ml-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
          </div>
        ))}
      </div>

      <p className="relative mt-6 text-[13.5px] leading-[1.6] text-white/65">
        <span className="font-semibold text-white">{t("Një platformë.", "One platform.")}</span>{" "}
        {t("Të gjitha proceset.", "Every process.")}{" "}
        <span className="font-semibold text-emerald-300">{t("Zero kaos.", "Zero chaos.")}</span>
      </p>
    </div>
  );
}
